import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import {
  USER_V3_ENDPOINTS,
  getEndpointInfo,
} from './apiEndPoints';

// Types
export interface CorridorPricingRequest {
  origin_country: string;
  destination_country: string;
  status?: string;
  currency_code?: string;
}

export interface CorridorPricingRow {
  weight_lb: number;
  extra_tier_percentage: string;
  min_price: string;
  base_currency?: string;
  display_min_price?: string;
  display_currency?: string;
  max_price: string;
  display_max_price?: string;
  sender_platform_fee: string;
  display_sender_platform_fee?: string;
  traveler_platform_fee: string;
  display_traveler_platform_fee?: string;
  sender_final_amount: string;
  display_sender_final_amount?: string;
  traveler_final_amount: string;
  display_traveler_final_amount?: string;
}

export interface CorridorPricingResponse {
  corridor_id: string;
  origin_country: string;
  origin_country_code: string;
  destination_country: string;
  destination_country_code: string;
  baseline_rate_per_lb: string;
  base_currency?: string;
  display_baseline_rate_per_lb?: string;
  display_currency?: string;
  multiplier: string;
  sender_fee_percentage: string;
  traveler_fee_percentage: string;
  pricing: CorridorPricingRow[];
}

export interface WeightPricingRequest {
  weight_lb: number;
  origin_country: string;
  destination_country: string;
  status?: string;
  currency_code?: string;
}

export interface CorridorCountry {
  id: string;
  name: string;
  code: string;
}

export type CorridorCountriesResponse = CorridorCountry[];

export interface UserProfileAddress {
  country_name: string;
  state_name: string;
  city_name: string;
  postal_code: string;
  street_no_one: string;
  street_no_two: string;
  longitude: string;
  latitude: string;
}

export interface UserProfileRole {
  role_id: string;
  name: string;
  status?: string;
}

export interface UserProfile {
  user_id: string;
  uuid: string;
  auth0_id: string;
  full_name: string;
  birthday: string;
  phone_number: string;
  user_role: string;
  roles?: UserProfileRole[];
  currency_id?: string;
  /** ISO currency code for the user's preferred display currency, e.g. "GBP" */
  code?: string;
  kyc_status?: string;
  phone_verified?: boolean;
  first_login?: boolean;
  card_verified?: boolean;
  negotiateID?: number;
  isSocialLogin?: boolean;
  is_deleted?: boolean;
  is_blocked?: boolean;
  has_admin_portal_access?: boolean;
  address: UserProfileAddress;
  profile_image: string;
  data_policy_accepted?: boolean;
}

export interface UpdateUserProfileRequest {
  fullName: string;
  birthday: string | null;
  contactNumber?: string;
  avatarUrl?: string;
  data_policy_accepted?: boolean;
  currency_id?: string;
  address: {
    country_name?: string;
    state_name?: string;
    city_name?: string;
    postalCode?: string;
    street1: string;
    street2?: string;
    latitude?: string;
    longitude?: string;
  };
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
}

export interface CurrenciesResponse {
  data: Currency[];
}

export interface UpdateUserCurrencyRequest {
  currency_id: string;
}

export interface WeightUnit {
  unit_code: string;
  unit_name: string;
  multiplier_from_lb: string;
}

export interface WeightUnitsResponse {
  units: WeightUnit[];
  formulas: Record<string, string>;
}

// Get user's latest orders (all matching orders instead of just one)
export interface LatestOrderParty {
  name: string;
  email: string;
  contact: string;
  profileImage: string;
}

export interface LatestOrderItem {
  sender: LatestOrderParty & { sender_id: string };
  traveler: LatestOrderParty & { traveler_id: string };
  order_id: string;
  checked_in_by_sender: boolean;
  checked_in_by_traveller: boolean;
  checked_out_by_traveller: boolean;
  is_checked_out_by_receiver: boolean;
  is_rated_by_sender: boolean;
  is_rated_by_traveler: boolean;
}

export interface LatestOrdersResponse {
  message: string;
  data: {
    role: 'sender' | 'traveler';
    latestOrders: LatestOrderItem[];
  };
}

export interface WeightPricingResponse {
  corridor_id: string;
  origin_country: string;
  origin_country_code: string;
  destination_country: string;
  destination_country_code: string;
  weight_lb: number;
  baseline_rate_per_lb: string;
  base_currency?: string;
  display_baseline_rate_per_lb?: string;
  display_currency?: string;
  multiplier: string;
  extra_tier_percentage: string;
  min_price: string;
  display_min_price?: string;
  max_price: string;
  display_max_price?: string;
  sender_fee_percentage: string;
  traveler_fee_percentage: string;
  sender_platform_fee: string;
  display_sender_platform_fee?: string;
  traveler_platform_fee: string;
  display_traveler_platform_fee?: string;
  sender_final_amount: string;
  display_sender_final_amount?: string;
  traveler_final_amount: string;
  display_traveler_final_amount?: string;
}

// Query keys
export const userV3Keys = {
  all: ['userV3'] as const,
  corridorPricing: (params: CorridorPricingRequest) =>
    ['userV3', 'corridorPricing', params] as const,
  weightPricing: (params: WeightPricingRequest) =>
    ['userV3', 'weightPricing', params] as const,
  corridorCountries: ['userV3', 'corridorCountries'] as const,
  profile: (auth_id: string) => ['userV3', 'profile', auth_id] as const,
  currencies: ['userV3', 'currencies'] as const,
  weightUnits: ['userV3', 'weightUnits'] as const,
  latestOrders: (user_id: string) => ['userV3', 'latestOrders', user_id] as const,
};

// API functions
const userApiV3Client = {
  async request({ url, method = 'GET', params, body, headers }: {
    url: string;
    method?: string;
    params?: Record<string, unknown>;
    body?: unknown;
    headers?: Record<string, string>;
  }) {
    try {
      const userService = getApiService(ServiceType.USER3);
      const response = await userService({
        url,
        method,
        params,
        data: body,
        headers,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  getCorridorPricing: (data: CorridorPricingRequest) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_CORRIDOR_PRICING);
    return userApiV3Client.request({
      url: endpoint.path,
      method: 'GET',
      params: {
        origin_country: data.origin_country,
        destination_country: data.destination_country,
        status: data.status,
        currency_code: data.currency_code,
      },
    });
  },

  getWeightPricing: (data: WeightPricingRequest) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_WEIGHT_PRICING);
    return userApiV3Client.request({
      url: endpoint.path,
      method: 'GET',
      params: {
        weight_lb: data.weight_lb,
        origin_country: data.origin_country,
        destination_country: data.destination_country,
        status: data.status,
        currency_code: data.currency_code,
      },
    });
  },

  getCorridorCountries: () => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_CORRIDOR_COUNTRIES);
    return userApiV3Client.request({ url: endpoint.path, method: 'GET' });
  },

  getUserProfile: (auth_id: string) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_USER_PROFILE);
    return userApiV3Client.request({
      url: endpoint.path.replace(':auth_id', auth_id),
      method: 'GET',
    });
  },

  updateUserProfile: (user_id: string, data: UpdateUserProfileRequest) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.UPDATE_USER_PROFILE);
    return userApiV3Client.request({
      url: endpoint.path.replace(':user_id', user_id),
      method: 'PUT',
      body: data,
    });
  },

  getCurrencies: () => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_CURRENCIES);
    return userApiV3Client.request({ url: endpoint.path, method: 'GET' });
  },

  updateUserCurrency: (user_id: string, data: UpdateUserCurrencyRequest) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.UPDATE_USER_CURRENCY);
    return userApiV3Client.request({
      url: endpoint.path.replace(':user_id', user_id),
      method: 'PATCH',
      body: data,
    });
  },

  getWeightUnits: () => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_WEIGHT_UNITS);
    return userApiV3Client.request({ url: endpoint.path, method: 'GET' });
  },

  getLatestOrders: (user_id: string) => {
    const endpoint = getEndpointInfo(USER_V3_ENDPOINTS.GET_LATEST_ORDERS);
    return userApiV3Client.request({
      url: endpoint.path.replace(':user_id', user_id),
      method: 'GET',
    });
  },
};

// Get corridor pricing query hook
export const useGetCorridorPricingQuery = (
  params: CorridorPricingRequest,
  options: Record<string, unknown> = {}
) =>
  useQuery<CorridorPricingResponse>({
    queryKey: userV3Keys.corridorPricing(params),
    queryFn: () => userApiV3Client.getCorridorPricing(params),
    enabled: !!params.origin_country && !!params.destination_country,
    ...options,
  });

// Get weight-based pricing query hook
export const useGetWeightPricingQuery = (
  params: WeightPricingRequest,
  options: Record<string, unknown> = {}
) =>
  useQuery<WeightPricingResponse>({
    queryKey: userV3Keys.weightPricing(params),
    queryFn: () => userApiV3Client.getWeightPricing(params),
    enabled: !!params.weight_lb && !!params.origin_country && !!params.destination_country,
    ...options,
  });

// Get active corridor countries query hook
export const useGetCorridorCountriesQuery = (options: Record<string, unknown> = {}) =>
  useQuery<CorridorCountriesResponse>({
    queryKey: userV3Keys.corridorCountries,
    queryFn: () => userApiV3Client.getCorridorCountries(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });

// Get user profile by Auth0 ID query hook
export const useGetUserProfileQuery = (auth_id: string, options: Record<string, unknown> = {}) =>
  useQuery<UserProfile>({
    queryKey: userV3Keys.profile(auth_id || ''),
    queryFn: () => userApiV3Client.getUserProfile(auth_id),
    enabled: !!auth_id && auth_id.length > 0,
    staleTime: 60 * 1000,
    ...options,
  });

// Update user profile mutation hook
export const useUpdateUserProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, data }: { user_id: string; data: UpdateUserProfileRequest }) =>
      userApiV3Client.updateUserProfile(user_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userV3Keys.all });
    },
  });
};

// Get list of available currencies query hook
export const useGetCurrenciesQuery = (options: Record<string, unknown> = {}) =>
  useQuery<CurrenciesResponse>({
    queryKey: userV3Keys.currencies,
    queryFn: () => userApiV3Client.getCurrencies(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });

// Update user's preferred display currency mutation hook
export const useUpdateUserCurrencyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, data }: { user_id: string; data: UpdateUserCurrencyRequest }) =>
      userApiV3Client.updateUserCurrency(user_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userV3Keys.all });
    },
  });
};

// Get list of supported weight units query hook
export const useGetWeightUnitsQuery = (options: Record<string, unknown> = {}) =>
  useQuery<WeightUnitsResponse>({
    queryKey: userV3Keys.weightUnits,
    queryFn: () => userApiV3Client.getWeightUnits(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });

// Get user's latest orders (all matching orders) query hook
export const useGetLatestOrdersQuery = (user_id: string, options: Record<string, unknown> = {}) =>
  useQuery<LatestOrdersResponse>({
    queryKey: userV3Keys.latestOrders(user_id),
    queryFn: () => userApiV3Client.getLatestOrders(user_id),
    enabled: !!user_id,
    ...options,
  });
